import { useState } from "react";
import { formatDisplayDate } from "../utils/dateFormat";
import DateInputField from "./DateInputField";

function ReservaForm({ form, onChange, onSubmit, canchas, reservas, bloques, clienteInputRef, disabled = false }) {
  const [mostrarDatosCliente, setMostrarDatosCliente] = useState(false);
  const horasDisponibles = Array.from({ length: 48 }, (_, index) => {
    const hour = String(Math.floor(index / 2)).padStart(2, "0");
    const minute = index % 2 === 0 ? "00" : "30";
    return `${hour}:${minute}`;
  });

  const toMinutes = (hora) => {
    const [h, m] = hora.split(":").map(Number);
    return (h * 60) + m;
  };

  const toHour = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const hasOverlap = (inicioA, finA, inicioB, finB) => inicioA < finB && inicioB < finA;
  const canchasRelacionadas = (canchaA, canchaB) => {
    if (canchaA === canchaB) {
      return true;
    }

    const esGrande = canchaA === "Cancha Grande";
    const esHijaDeGrande =
      canchaA === "Cancha mitad Norte" || canchaA === "Cancha mitad Sur";

    if (esHijaDeGrande && canchaB === "Cancha Grande") {
      return true;
    }

    if (
      esGrande &&
      (canchaB === "Cancha mitad Norte" || canchaB === "Cancha mitad Sur")
    ) {
      return true;
    }

    return false;
  };

  const reservasCanchaDia = (reservas || []).filter(
    (item) =>
      item.estado === "confirmada" &&
      canchasRelacionadas(form.cancha, item.cancha) &&
      item.fecha === form.fecha,
  );

  const getDayOfWeek = (fecha) => {
    const [year, month, day] = (fecha || "").split("-").map(Number);
    if (!year || !month || !day) {
      return null;
    }
    const jsDay = new Date(year, month - 1, day).getDay();
    return String((jsDay + 6) % 7); // 0=Lun ... 6=Dom
  };

  const dayOfWeek = getDayOfWeek(form.fecha);

  const bloquesCanchaDia = (bloques || [])
    .filter((item) => canchasRelacionadas(form.cancha, item.cancha))
    .flatMap((item) => {
      if (!dayOfWeek) {
        return [];
      }

      const isInRange = item.fecha_inicio <= form.fecha && form.fecha <= item.fecha_fin;
      if (!isInRange) {
        return [];
      }

      let dias = [];
      let franjas = [];
      try {
        dias = JSON.parse(item.dias_semana || "[]");
      } catch {
        dias = [];
      }
      try {
        franjas = JSON.parse(item.franjas || "[]");
      } catch {
        franjas = [];
      }

      if (!dias.includes(dayOfWeek)) {
        return [];
      }

      return franjas.map((franja, index) => ({
        bloque_id: `${item.id}-${index}`,
        cancha: item.cancha,
        hora_inicio: franja.inicio,
        hora_fin: franja.fin,
        descripcion: item.descripcion,
      }));
    });

  const reservasConfirmadasOrdenadas = reservasCanchaDia
    .map((reserva) => {
      const inicio = toMinutes(reserva.hora);
      const fin = inicio + Number(reserva.duracion_min || 60);
      return {
        ...reserva,
        inicio,
        fin,
      };
    })
    .sort((a, b) => a.inicio - b.inicio);

  const bloquesOrdenados = bloquesCanchaDia
    .map((bloque, index) => ({
      id: `bloque-${bloque.bloque_id || index}`,
      cliente: bloque.descripcion || "Bloque interno",
      inicio: toMinutes(bloque.hora_inicio),
      fin: toMinutes(bloque.hora_fin),
    }))
    .sort((a, b) => a.inicio - b.inicio);

  const ocupacionesOrdenadas = [
    ...reservasConfirmadasOrdenadas,
    ...bloquesOrdenados,
  ].sort((a, b) => a.inicio - b.inicio);

  const inicioJornada = 6 * 60;
  const finJornada = 23 * 60;
  const huecosDisponibles = [];

  let cursor = inicioJornada;
  ocupacionesOrdenadas.forEach((ocupacion) => {
    if (ocupacion.inicio > cursor) {
      huecosDisponibles.push({
        inicio: cursor,
        fin: ocupacion.inicio,
      });
    }
    cursor = Math.max(cursor, ocupacion.fin);
  });

  if (cursor < finJornada) {
    huecosDisponibles.push({ inicio: cursor, fin: finJornada });
  }

  const conflictoSeleccionado = form.hora
    ? ocupacionesOrdenadas.find((reserva) => {
      const inicioSeleccionado = toMinutes(form.hora);
      const finSeleccionado = inicioSeleccionado + Number(form.duracionMin || 60);
      const reservaInicio = reserva.inicio;
      const reservaFin = reserva.fin;
      return hasOverlap(inicioSeleccionado, finSeleccionado, reservaInicio, reservaFin);
    })
    : null;

  const setDuracion = (value) => {
    onChange({ target: { name: "duracionMin", value: String(value), type: "number" } });
  };

  const changeDuracion = (delta) => {
    const actual = Number(form.duracionMin || 60);
    const siguiente = Math.max(60, Math.min(180, actual + delta));
    setDuracion(siguiente);
  };

  const formatDuracion = (min) => {
    const minutos = Number(min);
    if (minutos === 60) {
      return "1 hora";
    }
    if (minutos % 60 === 0) {
      const horas = minutos / 60;
      return `${horas} horas`;
    }
    const horasEnteras = Math.floor(minutos / 60);
    return horasEnteras === 1 ? "1 hora y media" : `${horasEnteras} horas y media`;
  };

  return (
    <article className="panel">
      <h2>Nueva reserva</h2>
      <form className="form-grid" onSubmit={onSubmit}>
        <label>
          <span className="visually-hidden">Cliente</span>
          <input
            ref={clienteInputRef}
            name="cliente"
            value={form.cliente}
            disabled={disabled}
            onChange={onChange}
            placeholder="Cliente"
            aria-label="Cliente"
            required
          />
        </label>

        <button
          type="button"
          className="toggle-client-fields"
          disabled={disabled}
          onClick={() => setMostrarDatosCliente((prev) => !prev)}
        >
          {mostrarDatosCliente ? "Ocultar datos del cliente" : "Mostrar datos del cliente"}
          <span className="toggle-arrow">{mostrarDatosCliente ? "⌃" : "⌄"}</span>
        </button>

        {mostrarDatosCliente ? (
          <>
            <label>
              <span className="visually-hidden">Ruc / Cédula</span>
              <input
                name="rucCedula"
                value={form.rucCedula || ""}
                disabled={disabled}
                onChange={onChange}
                placeholder="Ruc / Cédula"
                aria-label="Ruc / Cédula"
              />
            </label>
            <label>
              <span className="visually-hidden">Dirección</span>
              <input
                name="direccion"
                value={form.direccion || ""}
                disabled={disabled}
                onChange={onChange}
                placeholder="Dirección"
                aria-label="Dirección"
              />
            </label>
            <label>
              <span className="visually-hidden">Celular</span>
              <input
                name="celular"
                value={form.celular || ""}
                disabled={disabled}
                onChange={onChange}
                placeholder="Celular"
                aria-label="Celular"
              />
            </label>
            <label>
              <span className="visually-hidden">Mail</span>
              <input
                type="email"
                name="mail"
                value={form.mail || ""}
                disabled={disabled}
                onChange={onChange}
                placeholder="Mail"
                aria-label="Mail"
              />
            </label>
            <div className="form-divider" />
          </>
        ) : null}

        <label>
          <span className="visually-hidden">Cancha</span>
          <select name="cancha" value={form.cancha} onChange={onChange} aria-label="Cancha" disabled={disabled}>
            {canchas.length === 0 ? (
              <option value="">Sin canchas disponibles</option>
            ) : (
              <>
                <option value="" disabled>Cancha</option>
                {canchas.map((cancha) => (
                  <option key={cancha} value={cancha}>
                    {cancha}
                  </option>
                ))}
              </>
            )}
          </select>
        </label>
        <label>
          <span className="visually-hidden">Fecha</span>
          <DateInputField
            name="fecha"
            value={form.fecha}
            onChange={onChange}
            ariaLabel="Fecha"
            required
            disabled={disabled}
          />
        </label>
        <label>
          <span className="visually-hidden">Hora</span>
          <select name="hora" value={form.hora} onChange={onChange} aria-label="Hora" required disabled={disabled}>
            <option value="" disabled>Hora (24h)</option>
            {horasDisponibles.map((hora) => (
              <option key={hora} value={hora}>{hora}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="visually-hidden">Duración del juego</span>
          <div className="duration-choice-row">
            <div className="duration-stepper">
              <button type="button" className="duration-step" onClick={() => changeDuracion(-30)} disabled={disabled}>
                -
              </button>
              <span className="duration-value">{formatDuracion(form.duracionMin)}</span>
              <button type="button" className="duration-step" onClick={() => changeDuracion(30)} disabled={disabled}>
                +
              </button>
            </div>
          </div>
        </label>
        <label>
          <span className="visually-hidden">Monto</span>
          <input
            type="number"
            min="1"
            step="0.01"
            name="monto"
            value={form.monto}
            disabled={disabled}
            onChange={onChange}
            placeholder="Monto"
            aria-label="Monto"
            required
          />
        </label>

        {disabled ? <p className="status-warn">El día de hoy ya fue cerrado. Para seguir operando, usa otra fecha abierta.</p> : null}

        {form.cancha && form.fecha ? (
          <section className="quick-availability">
            <strong>Disponibilidad rápida ({form.cancha} · {formatDisplayDate(form.fecha)})</strong>
            <p className="quick-availability-copy">
              Se muestran reservas confirmadas, bloques internos y tramos libres en verde.
            </p>
            <div className="quick-occupied-list">
              {ocupacionesOrdenadas.length === 0 ? (
                <p className="empty-copy">No hay ocupaciones en esta cancha/fecha.</p>
              ) : (
                ocupacionesOrdenadas.map((reserva) => (
                  <div key={reserva.id} className="quick-occupied-item">
                    <strong>{toHour(reserva.inicio)} - {toHour(reserva.fin)}</strong>
                    <span>{reserva.cliente}</span>
                  </div>
                ))
              )}
            </div>
            <div className="quick-free-ranges">
              {huecosDisponibles.map((hueco, index) => (
                <span key={`${hueco.inicio}-${hueco.fin}-${index}`} className="quick-free-badge">
                  {toHour(hueco.inicio)} - {toHour(hueco.fin)}
                </span>
              ))}
            </div>
            {form.hora ? (
              conflictoSeleccionado ? (
                <p className="quick-status status-danger">
                  Se cruza con {conflictoSeleccionado.cliente} ({toHour(conflictoSeleccionado.inicio)})
                </p>
              ) : (
                <p className="quick-status status-ok">Horario libre para guardar</p>
              )
            ) : null}
          </section>
        ) : null}

        <label className="checkbox-row">
          <input type="checkbox" name="pagado" checked={form.pagado} onChange={onChange} disabled={disabled} />
          Pagado al registrar
        </label>
        <button type="submit" disabled={canchas.length === 0 || disabled}>
          Guardar reserva
        </button>
      </form>
    </article>
  );
}


export default ReservaForm;